import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft, 
    Mail, 
    Phone, 
    Building2, 
    Calendar,
    CheckCircle,
    Clock,
    Trash2,
    MessageSquare,
    RefreshCw,
    Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enquiryTypeLabels = {
    demo: 'Request a Demo',
    pricing: 'Pricing Information',
    features: 'Feature Questions',
    support: 'Technical Support',
    partnership: 'Partnership Enquiry',
    other: 'Other'
};

export default function ContactSubmissionsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showResolveDialog, setShowResolveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [resolveNotes, setResolveNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchSubmissions = useCallback(async () => {
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await axios.get(`${API_URL}/admin/contact-submissions`, { params });
            setSubmissions(response.data);
        } catch (error) {
            toast.error('Failed to load contact submissions');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    const handleResolve = async () => {
        if (!selectedSubmission) return;
        setActionLoading(true);
        try {
            await axios.patch(`${API_URL}/admin/contact-submissions/${selectedSubmission.id}/resolve`, null, {
                params: { notes: resolveNotes || undefined }
            });
            toast.success('Submission marked as resolved');
            setShowResolveDialog(false);
            setSelectedSubmission(null);
            setResolveNotes('');
            fetchSubmissions();
        } catch (error) {
            toast.error('Failed to resolve submission');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedSubmission) return;
        setActionLoading(true);
        try {
            await axios.delete(`${API_URL}/admin/contact-submissions/${selectedSubmission.id}`);
            toast.success('Submission deleted');
            setShowDeleteDialog(false);
            setSelectedSubmission(null);
            fetchSubmissions();
        } catch (error) {
            toast.error('Failed to delete submission');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const newCount = submissions.filter(s => s.status === 'new').length;

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-30">
                <div className="flex items-center h-14 px-4 gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/portal')} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-lg font-semibold">Contact Submissions</h1>
                        <p className="text-xs text-muted-foreground">
                            {newCount > 0 ? `${newCount} new enquiries` : 'All caught up!'}
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchSubmissions}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto p-4 space-y-4">
                {/* Filter */}
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Submissions</SelectItem>
                            <SelectItem value="new">New Only</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Submissions List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading submissions...</p>
                    </div>
                ) : submissions.length === 0 ? (
                    <Card>
                        <CardContent className="text-center py-12">
                            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">No contact submissions found</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {submissions.map((submission) => (
                            <Card 
                                key={submission.id} 
                                className={`cursor-pointer hover:shadow-md transition-shadow ${
                                    submission.status === 'new' ? 'border-l-4 border-l-primary' : ''
                                }`}
                                onClick={() => setSelectedSubmission(submission)}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold truncate">{submission.name}</h3>
                                                <Badge variant={submission.status === 'new' ? 'default' : 'secondary'}>
                                                    {submission.status === 'new' ? 'New' : 'Resolved'}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                                                <span className="flex items-center gap-1">
                                                    <Building2 className="w-3 h-3" />
                                                    {submission.organisation}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(submission.created_at)}
                                                </span>
                                            </div>
                                            <Badge variant="outline" className="text-xs">
                                                {enquiryTypeLabels[submission.enquiry_type] || submission.enquiry_type}
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>

            {/* Submission Detail Dialog */}
            <Dialog open={!!selectedSubmission && !showResolveDialog && !showDeleteDialog} onOpenChange={() => setSelectedSubmission(null)}>
                <DialogContent className="max-w-lg">
                    {selectedSubmission && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center justify-between">
                                    <DialogTitle>{selectedSubmission.name}</DialogTitle>
                                    <Badge variant={selectedSubmission.status === 'new' ? 'default' : 'secondary'}>
                                        {selectedSubmission.status === 'new' ? 'New' : 'Resolved'}
                                    </Badge>
                                </div>
                                <DialogDescription>
                                    {enquiryTypeLabels[selectedSubmission.enquiry_type] || selectedSubmission.enquiry_type}
                                </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <a href={`mailto:${selectedSubmission.email}`} className="text-primary hover:underline">
                                            {selectedSubmission.email}
                                        </a>
                                    </div>
                                    {selectedSubmission.phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <a href={`tel:${selectedSubmission.phone}`} className="text-primary hover:underline">
                                                {selectedSubmission.phone}
                                            </a>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 col-span-2">
                                        <Building2 className="w-4 h-4 text-muted-foreground" />
                                        {selectedSubmission.organisation}
                                    </div>
                                    <div className="flex items-center gap-2 col-span-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        {formatDate(selectedSubmission.created_at)}
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-medium mb-2">Message</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-3 rounded-lg">
                                        {selectedSubmission.message}
                                    </p>
                                </div>

                                {selectedSubmission.notes && (
                                    <div className="border-t pt-4">
                                        <h4 className="text-sm font-medium mb-2">Resolution Notes</h4>
                                        <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                                            {selectedSubmission.notes}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <DialogFooter className="flex-col sm:flex-row gap-2">
                                {selectedSubmission.status === 'new' && (
                                    <Button onClick={() => setShowResolveDialog(true)} className="w-full sm:w-auto">
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Mark Resolved
                                    </Button>
                                )}
                                {user?.role === 'network_administrator' && (
                                    <Button 
                                        variant="destructive" 
                                        onClick={() => setShowDeleteDialog(true)}
                                        className="w-full sm:w-auto"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                    </Button>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Resolve Dialog */}
            <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Mark as Resolved</DialogTitle>
                        <DialogDescription>
                            Add any notes about how this enquiry was handled.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        placeholder="Optional notes..."
                        value={resolveNotes}
                        onChange={(e) => setResolveNotes(e.target.value)}
                        className="min-h-[100px]"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleResolve} disabled={actionLoading}>
                            {actionLoading ? 'Saving...' : 'Mark Resolved'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Submission</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this contact submission? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
                            {actionLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
